import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
  IconButton,
  List,
  ListItem,
} from '@mui/material';
import Delete from '@mui/icons-material/Delete';
import { isReminderMacro } from '../../utils/reminderMacro';
import {
  alternarSelecao,
  filtrarMacros,
  mensagemDoLote,
  resumoDoLote,
  selecaoVisivel,
} from '../../utils/selecaoDeMacros';
import Add from '@mui/icons-material/Add';
import { ThemeContext } from '../ThemeContext/ThemeContext';
import http from '../../services/http';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const ACTION_TYPES = [
  { value: 'set_ticket_type', label: 'Alterar tipo (interno/público)' },
  { value: 'set_status', label: 'Alterar status' },
  { value: 'set_service', label: 'Alterar serviço' },
  { value: 'set_category', label: 'Alterar categoria' },
  { value: 'set_urgency', label: 'Alterar urgência' },
  { value: 'set_responsible', label: 'Alterar responsável' },
  { value: 'set_subject', label: 'Definir assunto' },
  { value: 'set_message_type', label: 'Tipo da mensagem (pública/interna)' },
  { value: 'set_message_body', label: 'Texto da mensagem' },
  { value: 'set_attachments', label: 'Anexos da mensagem' },
  { value: 'add_cc', label: 'Incluir CC' },
  { value: 'remove_cc', label: 'Remover CC' },
];

const quillModules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link'],
    ['clean'],
  ],
};

const MacroConfig = () => {
  const { theme } = useContext(ThemeContext);
  const dialogFieldSx = useMemo(
    () => ({
      '& .MuiInputLabel-root': { color: `${theme.palette.text.secondary} !important` },
      '& .MuiOutlinedInput-root': {
        color: `${theme.palette.text.primary} !important`,
        '& fieldset': { borderColor: `${theme.palette.divider} !important` },
        '&:hover fieldset': { borderColor: `${theme.palette.text.secondary} !important` },
        '&.Mui-focused fieldset': { borderColor: `${theme.palette.primary.main} !important` },
      },
      '& .MuiSelect-icon': { color: `${theme.palette.text.secondary} !important` },
    }),
    [theme]
  );
  const macroMessageQuillWrapperSx = useMemo(
    () => ({
      '& .ql-toolbar': {
        border: `1px solid ${theme.palette.divider} !important`,
        borderBottom: 'none',
        borderRadius: '12px 12px 0 0',
        backgroundColor: `${theme.palette.background.default} !important`,
        padding: '10px 12px',
      },
      '& .ql-toolbar .ql-stroke': {
        stroke: `${theme.palette.text.primary} !important`,
      },
      '& .ql-toolbar .ql-fill': {
        fill: `${theme.palette.text.primary} !important`,
      },
      '& .ql-toolbar .ql-picker-label': {
        color: `${theme.palette.text.primary} !important`,
      },
      '& .ql-toolbar .ql-picker-options': {
        backgroundColor: `${theme.palette.background.paper} !important`,
        color: `${theme.palette.text.primary} !important`,
      },
      '& .ql-container': {
        border: `1px solid ${theme.palette.divider} !important`,
        borderTop: 'none',
        borderRadius: '0 0 12px 12px',
        fontSize: '15px',
        fontFamily: 'inherit',
        backgroundColor: `${theme.palette.background.paper} !important`,
      },
      '& .ql-editor': {
        minHeight: 180,
        padding: '10px 14px',
        color: `${theme.palette.text.primary} !important`,
        backgroundColor: `${theme.palette.background.paper} !important`,
      },
      '& .ql-editor strong, & .ql-editor b': { fontWeight: 700 },
      '& .ql-editor em, & .ql-editor i': { fontStyle: 'italic' },
      '& .ql-editor.ql-blank::before': {
        fontStyle: 'normal',
        color: `${theme.palette.text.disabled} !important`,
      },
    }),
    [theme]
  );
  const [macros, setMacros] = useState([]);
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  /*
   * Busca e seleção múltipla (pedido da Gisele, 19/10/2026). Com vinte macros
   * em cartões soltos, achar uma era varrer a tela com os olhos e desabilitar
   * cinco era abrir cinco vezes o formulário de edição.
   *
   * As regras (o que a busca olha, e o recorte da seleção pelo que está em
   * tela) moram em `utils/selecaoDeMacros`, com teste.
   */
  const [buscaDeMacro, setBuscaDeMacro] = useState('');
  const [selecionadas, setSelecionadas] = useState([]);
  const [aplicandoLote, setAplicandoLote] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [actionDialogOpen, setActionDialogOpen] = useState(false);
  const [actionEditIndex, setActionEditIndex] = useState(null);
  const [actionForm, setActionForm] = useState({ type: '', value: true, status_id: '', service_id: '', category: '', priority: 0, assigned_to: '', title: '', is_internal: false, body: '', attachments: [], user_ids: [] });
  // Texto do CC ainda não "chipado" (evita perder o e-mail digitado sem Enter).
  const [ccInput, setCcInput] = useState('');
  const [form, setForm] = useState({
    name: '',
    is_enabled: true,
    sharing_type: 'personal',
    team_ids: [],
    user_ids: [],
    actions: [],
    keywords: [],
  });

  const loadMacros = async () => {
    try {
      const { data } = await http.get('/macros');
      // Lembrete padrão sempre primeiro na lista (é único e serve de referência).
      const list = Array.isArray(data) ? [...data] : [];
      list.sort((a, b) => (isReminderMacro(b) ? 1 : 0) - (isReminderMacro(a) ? 1 : 0));
      setMacros(list);
    } catch (e) {
      toast.error('Erro ao carregar macros.');
    }
  };

  const loadOptions = async () => {
    try {
      const [g, u, st, sv, cat] = await Promise.all([
        http.get('/groups').then((r) => r.data || []),
        http.get('/users/list').then((r) => (Array.isArray(r.data) ? r.data : [])),
        http.get('/ticket-statuses').then((r) => r.data || []),
        http.get('/services').then((r) => r.data || []),
        http.get('/ticket-categories').then((r) => r.data || []),
      ]);
      setGroups(g);
      setUsers(u);
      setStatuses(st);
      const flatServices = (sv || []).flatMap((s) => [s, ...(s.subservices || []).map((sub) => ({ ...sub, name: `${s.name} → ${sub.name}` }))]);
      setServices(flatServices);
      setCategories(cat || []);
    } catch (e) {
      toast.error('Erro ao carregar opções.');
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await loadOptions();
      await loadMacros();
      setLoading(false);
    })();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({ name: '', is_enabled: true, sharing_type: 'personal', team_ids: [], user_ids: [], actions: [], keywords: [] });
    setModalOpen(true);
  };

  const openEdit = (m) => {
    setEditingId(m.id);
    setForm({
      name: m.name,
      is_enabled: m.is_enabled !== false,
      sharing_type: m.sharing_type || 'personal',
      team_ids: (m.teams || []).map((t) => t.id),
      user_ids: (m.shared_users || m.sharedUsers || []).map((u) => u.id),
      actions: Array.isArray(m.actions) ? [...m.actions] : [],
      keywords: Array.isArray(m.keywords) ? [...m.keywords] : [],
    });
    setModalOpen(true);
  };

  const getActionLabel = (action) => {
    const t = ACTION_TYPES.find((a) => a.value === action.type);
    if (!t) return action.type;
    if (action.type === 'set_ticket_type') return `${t.label}: ${action.value ? 'Interno' : 'Público'}`;
    if (action.type === 'set_status') return `${t.label}: ${statuses.find((s) => s.id === action.status_id)?.name ?? action.status_id}`;
    if (action.type === 'set_service') return `${t.label}: ${services.find((s) => s.id === action.service_id)?.name ?? action.service_id}`;
    if (action.type === 'set_category') return `${t.label}: ${action.category || '-'}`;
    if (action.type === 'set_urgency') return `${t.label}: ${['Baixa','Média','Alta','Urgente'][action.priority] ?? action.priority}`;
    if (action.type === 'set_responsible') return `${t.label}: ${users.find((u) => u.id === action.assigned_to)?.name ?? action.assigned_to}`;
    if (action.type === 'set_subject') return `${t.label}: ${(action.title || '').slice(0, 30)}...`;
    if (action.type === 'set_message_type') return `${t.label}: ${action.is_internal ? 'Interna' : 'Pública'}`;
    if (action.type === 'set_message_body') return `${t.label}: ${(action.body || '').replace(/<[^>]*>/g,'').slice(0, 40)}...`;
    if (action.type === 'set_attachments') return `${t.label}: ${(action.attachments || []).length} arquivo(s)`;
    if (action.type === 'add_cc' || action.type === 'remove_cc') return `${t.label}: ${((action.user_ids || []).length + (action.cc_emails || []).length)} destinatário(s)`;
    return t.label;
  };

  const openAddAction = (index = null) => {
    if (index != null && form.actions[index]) {
      const a = form.actions[index];
      setActionForm({
        type: a.type,
        value: a.value !== false,
        status_id: a.status_id ?? '',
        service_id: a.service_id ?? '',
        category: a.category ?? '',
        priority: a.priority ?? 0,
        assigned_to: a.assigned_to ?? '',
        title: a.title ?? '',
        is_internal: a.is_internal === true,
        body: a.body ?? '',
        attachments: a.attachments ?? [],
        user_ids: a.user_ids ?? [],
        cc_emails: a.cc_emails ?? [],
      });
      setActionEditIndex(index);
    } else {
      setActionForm({ type: '', value: true, status_id: '', service_id: '', category: '', priority: 0, assigned_to: '', title: '', is_internal: false, body: '', attachments: [], user_ids: [], cc_emails: [] });
      setActionEditIndex(null);
    }
    setCcInput('');
    setActionDialogOpen(true);
  };

  const saveAction = () => {
    const type = actionForm.type;
    if (!type) {
      toast.warn('Selecione o tipo de ação.');
      return;
    }
    let payload = { type };
    if (type === 'set_ticket_type') payload.value = actionForm.value;
    if (type === 'set_status') payload.status_id = actionForm.status_id || null;
    if (type === 'set_service') payload.service_id = actionForm.service_id || null;
    if (type === 'set_category') payload.category = actionForm.category || null;
    if (type === 'set_urgency') payload.priority = actionForm.priority;
    if (type === 'set_responsible') payload.assigned_to = actionForm.assigned_to || null;
    if (type === 'set_subject') payload.title = actionForm.title || '';
    if (type === 'set_message_type') payload.is_internal = actionForm.is_internal;
    if (type === 'set_message_body') payload.body = actionForm.body || '';
    if (type === 'set_attachments') payload.attachments = actionForm.attachments || [];
    if (type === 'add_cc' || type === 'remove_cc') {
      payload.user_ids = actionForm.user_ids || [];
      // Inclui o e-mail que ficou digitado no campo (sem Enter) na hora de salvar.
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const pending = (ccInput || '').trim();
      const emails = [...(actionForm.cc_emails || [])];
      if (pending && emailRegex.test(pending) && !emails.includes(pending)) emails.push(pending);
      payload.cc_emails = emails;
    }
    setCcInput('');
    const newActions = [...form.actions];
    if (actionEditIndex != null) {
      newActions[actionEditIndex] = payload;
    } else {
      newActions.push(payload);
    }
    setForm({ ...form, actions: newActions });
    setActionDialogOpen(false);
  };

  const removeAction = (index) => {
    setForm({ ...form, actions: form.actions.filter((_, i) => i !== index) });
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    try {
      for (let i = 0; i < files.length; i++) {
        const fd = new FormData();
        fd.append('file', files[i]);
        const { data } = await http.post('/macros/upload-attachment', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        setActionForm((prev) => ({ ...prev, attachments: [...(prev.attachments || []), { path: data.path, original_name: data.original_name }] }));
      }
      toast.success('Anexo(s) adicionado(s).');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Erro ao enviar anexo.');
    }
  };

  const handleSave = async () => {
    if (!form.name?.trim()) {
      toast.warn('Informe o nome da macro.');
      return;
    }
    const payload = {
      name: form.name.trim(),
      is_enabled: form.is_enabled,
      sharing_type: form.sharing_type,
      actions: form.actions,
      keywords: (form.keywords || []).map((k) => String(k).trim()).filter(Boolean),
      team_ids: form.sharing_type === 'selected_teams' ? form.team_ids : undefined,
      user_ids: form.sharing_type === 'selected_agents' ? form.user_ids : undefined,
    };
    try {
      if (editingId) {
        await http.put(`/macros/${editingId}`, payload);
        toast.success('Macro atualizada.');
      } else {
        await http.post('/macros', payload);
        toast.success('Macro criada.');
      }
      setModalOpen(false);
      await loadMacros();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao salvar.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta macro?')) return;
    try {
      await http.delete(`/macros/${id}`);
      toast.success('Macro excluída.');
      await loadMacros();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao excluir.');
    }
  };

  /*
   * As macros em tela, e a seleção recortada por elas.
   *
   * `selecaoEfetiva` é o que as ações em lote usam: quem marca três macros,
   * digita uma busca e clica em "Desativar" espera desativar o que ESTÁ
   * VENDO. Sem o recorte, a ação atingiria macros escondidas pela busca --
   * mudança invisível, que é o pior tipo.
   */
  const macrosVisiveis = useMemo(() => filtrarMacros(macros, buscaDeMacro), [macros, buscaDeMacro]);
  const selecaoEfetiva = useMemo(
    () => selecaoVisivel(selecionadas, macrosVisiveis),
    [selecionadas, macrosVisiveis],
  );

  /**
   * Ativa ou desativa as macros selecionadas.
   *
   * Um PUT por macro, e não um endpoint de lote: o servidor decide por macro
   * quem pode mexer (só o criador, ou quem tem permissão de gerenciar), e a
   * recusa de uma não pode derrubar as outras. Por isso `allSettled` -- e o
   * aviso conta os dois lados, em vez de dizer "pronto" ou "deu erro".
   */
  const aplicarEmLote = async (ativando) => {
    if (selecaoEfetiva.length === 0 || aplicandoLote) return;
    setAplicandoLote(true);
    try {
      const resultados = await Promise.allSettled(
        selecaoEfetiva.map((id) => http.put(`/macros/${id}`, { is_enabled: ativando })),
      );
      const resumo = resumoDoLote(resultados.map((r) => ({ ok: r.status === 'fulfilled' })));

      if (resumo.falhas === 0) toast.success(mensagemDoLote(resumo, ativando));
      else if (resumo.ok === 0) toast.error(mensagemDoLote(resumo, ativando));
      else toast.warn(mensagemDoLote(resumo, ativando));

      setSelecionadas([]);
      await loadMacros();
    } finally {
      setAplicandoLote(false);
    }
  };

  // Duplica a macro (item 23) — cria "Cópia de ..." para ajustar só o necessário.
  const handleClone = async (id) => {
    try {
      await http.post(`/macros/${id}/clone`);
      toast.success('Macro duplicada.');
      await loadMacros();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Não foi possível duplicar a macro.');
    }
  };

  if (loading) return <Typography color="text.secondary">Carregando...</Typography>;

  return (
    <Card elevation={0} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 2 }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: theme.palette.text.primary }}>
          Macros
        </Typography>
        <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
          Crie macros para preencher tickets ou enviar mensagens rapidamente. Defina compartilhamento (uso pessoal, todas as equipes, equipes ou agentes selecionados) e as ações (tipo do ticket, status, serviço, categoria, urgência, responsável, assunto, tipo e texto da mensagem, anexos, CC).
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
          <Button variant="contained" onClick={openCreate} sx={{ textTransform: 'none' }}>
            Nova macro
          </Button>
          <TextField
            size="small"
            placeholder="Buscar macro..."
            value={buscaDeMacro}
            onChange={(e) => setBuscaDeMacro(e.target.value)}
            inputProps={{ 'aria-label': 'Buscar macro' }}
            sx={{ width: 240 }}
            helperText="Procura no nome e nas palavras-chave"
            FormHelperTextProps={{ sx: { mx: 0 } }}
          />
        </Box>

        {/*
          * A barra de ações em lote só aparece com algo selecionado: botão que
          * não faz nada ocupando a tela o tempo todo é ruído, e aqui ele seria
          * um botão de ATIVAR/DESATIVAR sem alvo.
          */}
        {selecaoEfetiva.length > 0 && (
          <Box
            data-testid="barra-de-selecao-de-macros"
            sx={{
              display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 2, p: 1.25,
              borderRadius: 2, border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.action.hover,
            }}
          >
            <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 600 }}>
              {selecaoEfetiva.length} selecionada{selecaoEfetiva.length === 1 ? '' : 's'}
            </Typography>
            <Button size="small" variant="outlined" disabled={aplicandoLote} onClick={() => aplicarEmLote(true)} sx={{ textTransform: 'none' }}>
              Ativar
            </Button>
            <Button size="small" variant="outlined" disabled={aplicandoLote} onClick={() => aplicarEmLote(false)} sx={{ textTransform: 'none' }}>
              Desativar
            </Button>
            <Button size="small" disabled={aplicandoLote} onClick={() => setSelecionadas([])} sx={{ textTransform: 'none' }}>
              Limpar seleção
            </Button>
          </Box>
        )}

        {macrosVisiveis.length === 0 && (
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mb: 2 }}>
            {buscaDeMacro.trim() ? 'Nenhuma macro encontrada.' : 'Nenhuma macro cadastrada.'}
          </Typography>
        )}

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {macrosVisiveis.map((m) => (
            <Card key={m.id} variant="outlined" sx={{ minWidth: 200, borderRadius: 2 }}>
              <CardContent sx={{ py: 1.5, px: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                    <Checkbox
                      size="small"
                      checked={selecaoEfetiva.some((id) => String(id) === String(m.id))}
                      onChange={() => setSelecionadas((atual) => alternarSelecao(atual, m.id))}
                      inputProps={{ 'aria-label': `Selecionar ${m.name}` }}
                      sx={{ p: 0.5, mt: -0.25 }}
                    />
                    <Box>
                    <Typography variant="subtitle2" fontWeight={600}>{m.name}</Typography>
                    {isReminderMacro(m) && (
                      <Tooltip title="O texto desta macro é o lembrete que aparece na caixa de ação do ticket novo. Pode ser editado, mas não excluído nem renomeado." arrow>
                        <Chip size="small" label="Lembrete da caixa de ação" color="primary" variant="outlined" sx={{ mt: 0.5, mr: 0.5 }} />
                      </Tooltip>
                    )}
                    {!m.is_enabled && <Chip size="small" label="Desabilitada" color="default" sx={{ mt: 0.5 }} />}
                    <Typography variant="caption" display="block" sx={{ color: theme.palette.text.secondary }}>
                      {m.sharing_type === 'personal' && 'Uso pessoal'}
                      {m.sharing_type === 'all_teams' && 'Todas as equipes'}
                      {m.sharing_type === 'selected_teams' && `${(m.teams || []).length} equipe(s)`}
                      {m.sharing_type === 'selected_agents' && `${(m.shared_users || m.sharedUsers || []).length} agente(s)`}
                    </Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Button size="small" onClick={() => openEdit(m)} sx={{ textTransform: 'none', mr: 0.5 }}>Editar</Button>
                    {/* O lembrete padrão é único: não duplica nem exclui. */}
                    {!isReminderMacro(m) && (
                      <Button size="small" onClick={() => handleClone(m.id)} sx={{ textTransform: 'none', mr: 0.5 }}>Duplicar</Button>
                    )}
                    {!isReminderMacro(m) && (
                      <Button size="small" color="error" onClick={() => handleDelete(m.id)} sx={{ textTransform: 'none' }}>Excluir</Button>
                    )}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>{editingId ? 'Editar macro' : 'Nova macro'}</DialogTitle>
          <DialogContent>
            <TextField fullWidth label="Nome" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} sx={{ mt: 1, mb: 2 }} />
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={form.keywords || []}
              onChange={(e, v) => setForm({ ...form, keywords: v.map((k) => String(k).trim()).filter(Boolean) })}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip size="small" label={option} {...getTagProps({ index })} key={`${option}-${index}`} />
                ))
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Palavras-chave"
                  placeholder="Digite e tecle Enter"
                  helperText="Ao digitar uma destas palavras na ação do ticket, esta macro é sugerida. Aceitar substitui todo o texto da ação."
                />
              )}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={<Switch checked={form.is_enabled} onChange={(e) => setForm({ ...form, is_enabled: e.target.checked })} />}
              label="Habilitada (desabilitadas não aparecem na lista de macros nos tickets)"
            />
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Compartilhamento</Typography>
            <Select fullWidth size="small" value={form.sharing_type} onChange={(e) => setForm({ ...form, sharing_type: e.target.value })} sx={{ mb: 1 }}>
              <MenuItem value="personal">Apenas meu uso</MenuItem>
              <MenuItem value="all_teams">Todas as equipes</MenuItem>
              <MenuItem value="selected_teams">Equipes selecionadas</MenuItem>
              <MenuItem value="selected_agents">Agentes (usuários) selecionados</MenuItem>
            </Select>
            {form.sharing_type === 'selected_teams' && (
              <Select fullWidth multiple size="small" value={form.team_ids} onChange={(e) => setForm({ ...form, team_ids: e.target.value })} renderValue={(s) => s.map((id) => groups.find((g) => g.id === id)?.description || id).join(', ')} sx={{ mb: 1 }}>
                {groups.map((g) => (
                  <MenuItem key={g.id} value={g.id}><Checkbox checked={form.team_ids.indexOf(g.id) > -1} /><ListItemText primary={g.description || g.code || `Equipe ${g.id}`} /></MenuItem>
                ))}
              </Select>
            )}
            {form.sharing_type === 'selected_agents' && (
              <Select fullWidth multiple size="small" value={form.user_ids} onChange={(e) => setForm({ ...form, user_ids: e.target.value })} renderValue={(s) => s.map((id) => users.find((u) => u.id === id)?.name || id).join(', ')} sx={{ mb: 1 }}>
                {users.map((u) => (
                  <MenuItem key={u.id} value={u.id}><Checkbox checked={form.user_ids.indexOf(u.id) > -1} /><ListItemText primary={u.name} /></MenuItem>
                ))}
              </Select>
            )}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Ações</Typography>
            <List dense>
              {form.actions.map((action, idx) => (
                <ListItem key={idx} secondaryAction={<IconButton size="small" onClick={() => removeAction(idx)}><Delete /></IconButton>}>
                  <Typography variant="body2" onClick={() => openAddAction(idx)} sx={{ cursor: 'pointer', flex: 1 }}>{getActionLabel(action)}</Typography>
                  <Button size="small" onClick={() => openAddAction(idx)}>Editar</Button>
                </ListItem>
              ))}
            </List>
            <Button startIcon={<Add />} onClick={() => openAddAction()} size="small" sx={{ mt: 1 }}>Adicionar ação</Button>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setModalOpen(false)}>Cancelar</Button>
            <Button variant="contained" onClick={handleSave}>Salvar</Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={actionDialogOpen}
          onClose={() => setActionDialogOpen(false)}
          maxWidth={actionForm.type === 'set_message_body' ? 'md' : 'lg'}
          fullWidth
        >
          <DialogTitle sx={{ color: `${theme.palette.text.primary} !important` }}>Adicionar / editar ação</DialogTitle>
          <DialogContent sx={{ color: `${theme.palette.text.primary} !important` }}>
            <Typography variant="caption" sx={{ mb: 1, display: 'block', color: `${theme.palette.text.secondary} !important` }}>
              Tipo
            </Typography>
            <Select
              fullWidth
              size="small"
              value={actionForm.type}
              onChange={(e) => setActionForm({ ...actionForm, type: e.target.value })}
              displayEmpty
              sx={{ mb: 2, ...dialogFieldSx }}
            >
              <MenuItem value="">Selecione</MenuItem>
              {ACTION_TYPES.map((a) => (
                <MenuItem key={a.value} value={a.value}>{a.label}</MenuItem>
              ))}
            </Select>
            {actionForm.type === 'set_ticket_type' && (
              <FormControlLabel control={<Switch checked={actionForm.value} onChange={(e) => setActionForm({ ...actionForm, value: e.target.checked })} />} label="Ticket interno" />
            )}
            {actionForm.type === 'set_status' && (
              <Select fullWidth size="small" value={actionForm.status_id} onChange={(e) => setActionForm({ ...actionForm, status_id: e.target.value })} displayEmpty sx={{ mb: 1 }}><MenuItem value="">Nenhum</MenuItem>{statuses.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}</Select>
            )}
            {actionForm.type === 'set_service' && (
              <Select fullWidth size="small" value={actionForm.service_id} onChange={(e) => setActionForm({ ...actionForm, service_id: e.target.value })} displayEmpty sx={{ mb: 1 }}><MenuItem value="">Nenhum</MenuItem>{services.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}</Select>
            )}
            {actionForm.type === 'set_category' && (
              <Select fullWidth size="small" value={actionForm.category} onChange={(e) => setActionForm({ ...actionForm, category: e.target.value })} displayEmpty sx={{ mb: 1 }}><MenuItem value="">Nenhuma</MenuItem>{categories.map((c) => <MenuItem key={c.id} value={c.name}>{c.name}</MenuItem>)}</Select>
            )}
            {actionForm.type === 'set_urgency' && (
              <Select fullWidth size="small" value={actionForm.priority} onChange={(e) => setActionForm({ ...actionForm, priority: Number(e.target.value) })} sx={{ mb: 1 }}><MenuItem value={0}>Baixa</MenuItem><MenuItem value={1}>Média</MenuItem><MenuItem value={2}>Alta</MenuItem><MenuItem value={3}>Urgente</MenuItem></Select>
            )}
            {actionForm.type === 'set_responsible' && (
              <Select fullWidth size="small" value={actionForm.assigned_to} onChange={(e) => setActionForm({ ...actionForm, assigned_to: e.target.value })} displayEmpty sx={{ mb: 1 }}><MenuItem value="">Nenhum</MenuItem>{users.map((u) => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}</Select>
            )}
            {actionForm.type === 'set_subject' && (
              <TextField
                fullWidth
                size="small"
                label="Assunto"
                value={actionForm.title}
                onChange={(e) => setActionForm({ ...actionForm, title: e.target.value })}
                sx={{ mb: 1, ...dialogFieldSx }}
              />
            )}
            {actionForm.type === 'set_message_type' && (
              <FormControlLabel control={<Switch checked={actionForm.is_internal} onChange={(e) => setActionForm({ ...actionForm, is_internal: e.target.checked })} />} label="Mensagem interna" />
            )}
            {actionForm.type === 'set_message_body' && (
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ display: 'block', mb: 0.75, color: `${theme.palette.text.secondary} !important` }}
                >
                  Texto da mensagem
                </Typography>
                <Box sx={macroMessageQuillWrapperSx}>
                  <ReactQuill
                    value={actionForm.body}
                    onChange={(v) => setActionForm({ ...actionForm, body: v })}
                    modules={quillModules}
                    theme="snow"
                    placeholder="Digite o texto que será aplicado na caixa de mensagem do ticket"
                  />
                </Box>
              </Box>
            )}
            {actionForm.type === 'set_attachments' && (
              <Box sx={{ mb: 1 }}>
                <input type="file" multiple accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.txt,.mp3,.ogg,.webm,.wav" onChange={handleFileUpload} style={{ marginBottom: 8 }} />
                {(actionForm.attachments || []).map((att, i) => (
                  <Chip key={i} label={att.original_name} onDelete={() => setActionForm({ ...actionForm, attachments: actionForm.attachments.filter((_, j) => j !== i) })} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                ))}
              </Box>
            )}
            {(actionForm.type === 'add_cc' || actionForm.type === 'remove_cc') && (
              <Autocomplete
                multiple
                freeSolo
                size="small"
                inputValue={ccInput}
                onInputChange={(e, v, reason) => {
                  if (reason === 'reset') { setCcInput(''); return; }
                  setCcInput(v);
                }}
                onBlur={() => {
                  // Comita o e-mail digitado mesmo sem Enter (só se for válido).
                  const email = (ccInput || '').trim();
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  if (email && emailRegex.test(email)) {
                    setActionForm((prev) => ({ ...prev, cc_emails: [...new Set([...(prev.cc_emails || []), email])] }));
                  }
                  setCcInput('');
                }}
                options={users}
                getOptionLabel={(opt) => (typeof opt === 'string' ? opt : (opt?.name || opt?.email || ''))}
                isOptionEqualToValue={(opt, val) => typeof opt !== 'string' && typeof val !== 'string' && opt.id === val.id}
                value={[
                  ...users.filter((u) => (actionForm.user_ids || []).includes(u.id)),
                  ...(actionForm.cc_emails || []),
                ]}
                onChange={(e, newValue) => {
                  const ids = [];
                  const emails = [];
                  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                  let rejected = false;
                  newValue.forEach((v) => {
                    if (typeof v === 'string') {
                      const email = v.trim();
                      if (!email) return;
                      if (emailRegex.test(email)) {
                        emails.push(email);
                      } else {
                        rejected = true;
                      }
                    } else if (v && v.id != null) {
                      ids.push(v.id);
                    }
                  });
                  if (rejected) {
                    toast.warn('E-mail inválido ignorado.');
                  }
                  setActionForm({ ...actionForm, user_ids: [...new Set(ids)], cc_emails: [...new Set(emails)] });
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Selecione usuários ou digite e-mails"
                    helperText="Selecione operadores ou digite e-mails externos."
                  />
                )}
                sx={{ mb: 1 }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setActionDialogOpen(false)}>Cancelar</Button>
            <Button variant="contained" onClick={saveAction}>Adicionar</Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default MacroConfig;
